import React from 'react';
import _ from 'lodash';
import * as Yup from 'yup';

import { getNotificationPriorityOrDefault } from '~/components/Notifications/utils';
import type { OrganizationModel } from '~/components/types/organization-types';
import LEGACY_ORG_FEATURES from '~/server_shared/generated-types/LEGACY_ORG_FEATURES';
import type {
  NotificationRuleCreateJson,
  UpdatePreDefinedNotificationJson,
} from '~/server_shared/openapi/data-contracts';

import {
  CONFIGURATION_FEATURES_NAMES,
  COUNTRY_TO_STATE_MAP,
  NOTIFICATIONS_ENTITY_STATUSES,
  NOTIFICATIONS_RULES_RECIPIENTS,
  NOTIFICATIONS_RULES_TIMEFRAMES,
  NOTIFICATIONS_SCOPES,
} from '../../../../Types';
import { isFeatureEnabled, isLegacyOrgFeatureEnabled, isOrganizationUsOrg } from '../../../../Utils';
import { useOrganizationDetails } from '../../../hooks/useOrganizationDetails';
import {
  MandatoryNotificationIcon,
  RecommendedNotificationIcon,
  SelfConfiguredNotificationIcon,
} from '../../../icons/notifications';
import { isEmptyConditionsTree } from '../Conditions/QueryBuilder';

import type {
  NotificationConfigurationFormikFields,
  NotificationRuleFormikEventKey,
  NotificationRuleFormikValues,
  PreDefinedNotification,
} from './types';

type KeyDescOption = { key: string; desc?: string };

// A notification rule/condition as it arrives from the server, before Formik conversion.
interface ServerRuleCondition {
  criteria_key: string;
  criteria_types?: string;
  criteria_filters?: Record<string, unknown>;
  condition_schedule_value: number;
  condition_key?: string | null;
  condition_text_value?: string | null;
  condition_numeric_value?: number | null;
  condition_schedule_timeframe_value?: string | null;
}

// Loose rule shape accepted by ruleToInitialValue (raw server rule or an already-converted Formik rule).
interface RuleInitialInput {
  // The Duplicate action nulls this out to turn an edit into a create.
  id?: number | string | null;
  lob?: string;
  scope?: string;
  scope_status?: string;
  scope_types?: KeyDescOption[];
  scope_sub_types?: KeyDescOption[];
  event_key?: NotificationRuleFormikEventKey | null;
  event_criteria_types?: KeyDescOption[];
  is_immediate?: boolean;
  schedule_number?: number | null;
  schedule_timeframe?: string | null;
  recipient?: string;
  organization_unit_id?: number | null;
  rule_name?: string;
  title?: string;
  content?: string;
  sub_organization_ids?: (number | 'all')[];
  conditions?: ServerRuleCondition[];
  conditions_json?: Record<string, unknown> | null;
  is_all_states?: boolean;
  states?: string[];
  priority?: string;
  category_id?: number | null;
  is_due_date_immediate?: boolean;
  due_date_number?: number | null;
  due_date_timeframe?: string | null;
  rule_trigger_type?: string;
  time_based_condition?: NotificationRuleFormikValues['time_based_condition'];
  time_based_directions?: string[];
  time_based_channels?: string[];
  time_based_category_ids?: string[];
  time_based_stage_keys?: string[];
  time_based_recipient?: string[];
}

// Raw server rule consumed by convertServerRuleToFormikValues (scope_types etc. are joined strings here).
interface ServerNotificationRule {
  scope: string;
  scope_types: string;
  scope_sub_types?: string | null;
  event_criteria_types?: string;
  event_key?: string | null;
  is_all_sub_orgs: boolean;
  sub_organization_ids: number[];
  organization_unit_id?: number | null;
  is_due_date_immediate?: boolean;
  is_immediate?: boolean;
  rule_trigger_type?: string;
  conditions: ServerRuleCondition[];
  conditions_json?: Record<string, unknown> | null;
}

interface EventConfig {
  key: string;
  types?: KeyDescOption[];
}

interface CriteriaConditionGroup {
  options?: Record<string, Record<string, unknown>>;
  condition_value_type?: string;
  text_condition_values?: unknown;
}

interface CriteriaConfig {
  has_type?: boolean;
  types?: KeyDescOption[];
  conditions: CriteriaConditionGroup[];
}

type CriteriaConfigMap = Record<string, CriteriaConfig>;

interface ConditionWithGroupAttributes {
  additionalValueType?: string;
  textValues?: unknown;
  [key: string]: unknown;
}

type PreDefinedNotificationInput = PreDefinedNotification & { category_id?: number };

export const RULE_TYPE_OPTIONS = {
  mandatory: {
    title: 'Mandatory',
    icon: MandatoryNotificationIcon,
  },
  optional: {
    title: 'Recommended',
    icon: RecommendedNotificationIcon,
  },
  self_configured: {
    title: 'Self-configured',
    icon: SelfConfiguredNotificationIcon,
  },
  time_based: {
    title: 'Time-based',
    icon: SelfConfiguredNotificationIcon,
  },
};

export const ALL_OPTION = { desc: 'All', key: 'all' };

export const useNotificationConfigurationTool = () => {
  const { organization } = useOrganizationDetails({ kind: 'sysconfig' });

  const managerTerm = React.useMemo(() => {
    return isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.ORG_HIERARCHY_WORKFLOWS)
      ? 'unit leader'
      : 'supervisor';
  }, [organization]);

  const formatManagerTerm = React.useCallback(
    (recipientTypeDescription: string) => recipientTypeDescription.replace('{manager_term}', managerTerm),
    [managerTerm]
  );

  const recipientTypes = React.useMemo(
    () =>
      Object.fromEntries(
        Object.entries(NOTIFICATIONS_RULES_RECIPIENTS).map(([key, value]) => [
          key,
          { ...value, desc: formatManagerTerm(value.desc) },
        ])
      ),
    [formatManagerTerm]
  );

  return {
    recipientTypes,
    formatManagerTerm,
  };
};

export const getScopeTypeDescription = (
  scopeType: string,
  scope: string,
  incidentTypesMap: Record<string, string>,
  coveragesMap: Record<string, string>
): string => {
  if (scope === 'claim') {
    return incidentTypesMap[scopeType] || '';
  } else if (scope === 'exposure') {
    return coveragesMap[scopeType] || '';
  } else {
    throw Error(`Unknown notification scope ${scope} in NotificationConfigurationTool`);
  }
};

export const INITIAL_SEARCH_FILTER = {
  searchNotificationText: '',
  selectedRuleType: [],
  selectedSubOrgs: [],
  selectedScope: [],
  selectedScopeType: [],
  selectedRecipient: [],
  hideDisabled: false,
};

export const NOTIFICATION_TYPE = {
  MANDATORY: 'mandatory',
  OPTIONAL: 'optional',
  SELF_CONFIGURED: 'self_configured',
};

export const ruleToInitialValue = (rule: RuleInitialInput | null) => {
  const baseValues = {
    id: rule?.id || '',
    lob: rule?.lob || '',
    scope: rule?.scope || '',
    scope_status: rule?.scope_status || '',
    scope_types: rule?.scope_types || [ALL_OPTION],
    scope_sub_types: rule?.scope_sub_types || [ALL_OPTION],
    event_key: rule?.event_key || null,
    event_criteria_types: rule?.event_criteria_types || [ALL_OPTION],
    is_immediate: rule?.is_immediate ?? true,
    schedule_immediate_or_custom: !rule || rule?.is_immediate ? 'immediate' : 'custom',
    schedule_number: rule?.schedule_number,
    schedule_timeframe: rule?.schedule_timeframe,
    recipient: rule?.recipient || '',
    organization_unit_id: rule?.organization_unit_id,
    rule_name: rule?.rule_name || '',
    title: rule?.title || '',
    content: rule?.content || '',
    sub_organization_ids: rule?.sub_organization_ids || [],
    conditions: rule?.conditions || [],
    conditions_json: rule?.conditions_json || null,
    is_all_states: rule?.is_all_states ?? true,
    states: rule?.states ?? [],
    priority: rule?.priority ?? getNotificationPriorityOrDefault(),
    category_id: rule?.category_id,
    is_due_date_immediate: rule?.is_due_date_immediate ?? true,
    due_date_immediate_or_custom: !rule || rule?.is_due_date_immediate !== false ? 'immediate' : 'custom',
    due_date_number: rule?.due_date_number,
    due_date_timeframe: rule?.due_date_timeframe,
    // Time-based notification fields
    rule_trigger_type: rule?.rule_trigger_type || 'event_based',
    time_based_condition: null as NotificationRuleFormikValues['time_based_condition'],
    time_based_directions: ['all'],
    time_based_channels: ['all'],
    time_based_category_ids: ['all'],
    time_based_stage_keys: ['all'],
    time_based_recipient: ['all'],
  };

  // Extract time-based condition if rule is time-based
  // First check if time_based_condition is already set (from convertServerRuleToFormikValues)
  // Otherwise extract from conditions array (raw server data)
  if (rule?.rule_trigger_type === 'time_based') {
    if (rule.time_based_condition) {
      baseValues.time_based_condition = rule.time_based_condition;
    } else if ((rule.conditions?.length ?? 0) > 0) {
      const timeCondition = rule.conditions?.find(
        (c) => c.criteria_key === 'no_notes' || c.criteria_key === 'no_communication'
      );

      if (timeCondition) {
        baseValues.time_based_condition = {
          criteria_key: timeCondition.criteria_key,
          criteria_filters: timeCondition.criteria_filters || {},
          condition_schedule_value: timeCondition.condition_schedule_value,
        };
      }
    }

    // Extract filter values into separate Formik fields
    if (rule.time_based_directions) {
      baseValues.time_based_directions = rule.time_based_directions;
    }
    if (rule.time_based_channels) {
      baseValues.time_based_channels = rule.time_based_channels;
    }
    if (rule.time_based_category_ids) {
      baseValues.time_based_category_ids = rule.time_based_category_ids;
    }
    if (rule.time_based_stage_keys) {
      baseValues.time_based_stage_keys = rule.time_based_stage_keys;
    }
    if (rule.time_based_recipient) {
      baseValues.time_based_recipient = rule.time_based_recipient;
    }
  }

  return baseValues;
};

export const preDefinedToInitialValues = (notification: PreDefinedNotificationInput) => ({
  lob: notification.lob || '',
  scope: notification.scope || '',
  scope_types: notification.scope_types || [ALL_OPTION],
  recipient: notification.recipient || '',
  rule_name: notification.rule_name || '',
  title: notification.title || '',
  content: notification.content || '',
  sub_orgs: notification.sub_orgs || '',
  is_disabled: notification.is_disabled ?? false,
  is_all_states: notification.is_all_states ?? true,
  states: notification.states ?? [],
  category_id: notification.category_id,
  priority: notification.priority ?? getNotificationPriorityOrDefault(),
  is_due_date_immediate: notification.is_due_date_immediate ?? true,
  due_date_immediate_or_custom: notification.is_due_date_immediate !== false ? 'immediate' : 'custom',
  due_date_number: notification.due_date_number,
  due_date_timeframe: notification.due_date_timeframe,
  predefined_notification_conditions: {
    ...(notification.predefined_notification_conditions ?? {}),
  },
});

export const statesValidationSchema = {
  states: Yup.array(Yup.string()).when('is_all_states', (isAllStates, currSchema) => {
    if (!isAllStates) {
      return currSchema.min(1, 'At least one state is required if a notification is defined for specific states');
    }
    return currSchema;
  }),
};

export const notificationConfigurationValidationSchema = {
  ...statesValidationSchema,
  due_date_number: Yup.number()
    .when('is_due_date_immediate', {
      is: 'custom',
      then: Yup.number().nullable().required('Required'),
    })
    .nullable(),
  due_date_timeframe: Yup.string()
    .when('is_due_date_immediate', {
      is: 'custom',
      then: Yup.string()
        .nullable()
        .required('Required')
        .oneOf(Object.keys(NOTIFICATIONS_RULES_TIMEFRAMES), 'Invalid timeframe'),
    })
    .nullable(),
  predefined_notification_conditions: Yup.object()
    .shape({
      suppress_self_assignment: Yup.boolean(),
      suppress_approval_decision: Yup.boolean(),
    })
    .nullable(),
};

/** Which conditions editor a rule opens in: the flag, and event-based rules only.
 *
 * The flag alone decides — never row presence, never whether the rule already
 * holds a tree — so every rule an organization opens after the flip lands in the
 * same editor. Time-based rules are out of scope: their single condition row is
 * authored by `TimeBasedConditionFormik` and the builder's catalog has nothing
 * to offer them. */
export const shouldUseConditionsQueryBuilder = (
  isQueryBuilderFeatureEnabled: boolean,
  ruleTriggerType: string | null | undefined
): boolean => isQueryBuilderFeatureEnabled && (ruleTriggerType ?? 'event_based') === 'event_based';

export const getValidationSchemaShape = (
  existingRuleNames: string[],
  conditions: CriteriaConfigMap,
  subOrgsRequired: boolean,
  recipientTypes: Record<string, unknown>,
  isQueryBuilderFeatureEnabled: boolean
) => ({
  lob: Yup.string().required('Required'),
  scope: Yup.string().required('Required').oneOf(Object.keys(NOTIFICATIONS_SCOPES), 'Invalid entity'),
  scope_status: Yup.string()
    .required('Required')
    .oneOf(Object.keys(NOTIFICATIONS_ENTITY_STATUSES), 'Invalid entity status'),
  scope_types: Yup.array().min(1, 'At least one type is required'),
  rule_trigger_type: Yup.string().oneOf(['event_based', 'time_based']).required(),
  // Event-based fields (conditional)
  event_key: Yup.object()
    .when('rule_trigger_type', {
      is: 'event_based',
      then: Yup.object().nullable().required('Required'),
      otherwise: Yup.object().nullable(),
    })
    .nullable(),
  event_criteria_types: Yup.array().when(['rule_trigger_type', 'event_key'], {
    is: (ruleTriggerType: string, eventKey: NotificationRuleFormikEventKey | null) =>
      ruleTriggerType === 'event_based' && !!eventKey?.has_type,
    then: (currSchema) => currSchema.min(1, 'At least one type is required'),
  }),
  // Time-based condition (conditional)
  time_based_condition: Yup.object()
    .when('rule_trigger_type', {
      is: 'time_based',
      then: Yup.object({
        criteria_key: Yup.string().oneOf(['no_notes', 'no_communication']).required('Condition type is required'),
        criteria_filters: Yup.object().nullable(), // Optional - NULL/empty = ALL
        condition_schedule_value: Yup.number().min(1, 'Must be at least 1 day').required('Number of days is required'),
      })
        .nullable()
        .required('Time-based condition is required'),
      otherwise: Yup.object().nullable(),
    })
    .nullable(),
  schedule_number: Yup.number()
    .when(['schedule_immediate_or_custom', 'rule_trigger_type'], {
      is: (scheduleType: string, triggerType: string) => scheduleType === 'custom' && triggerType !== 'time_based',
      then: Yup.number().nullable().required('Required'),
    })
    // A time-based event notifies this long before the due date, and the server refuses less than 1.
    .when('event_key', {
      is: (eventKey: NotificationRuleFormikEventKey | null) => !!eventKey?.is_time_based,
      then: (currSchema) => currSchema.min(1, 'Must be at least 1'),
    })
    .nullable(),
  schedule_timeframe: Yup.string()
    .when(['schedule_immediate_or_custom', 'rule_trigger_type'], {
      is: (scheduleType: string, triggerType: string) => scheduleType === 'custom' && triggerType !== 'time_based',
      then: Yup.string()
        .nullable()
        .required('Required')
        .oneOf(Object.keys(NOTIFICATIONS_RULES_TIMEFRAMES), 'Invalid timeframe'),
    })
    .nullable(),
  recipient: Yup.string().required('Required').oneOf(Object.keys(recipientTypes), 'Invalid recipient'),
  organization_unit_id: Yup.number().when('recipient', {
    is: 'organization_unit_leader',
    then: Yup.number().typeError('Required').required('Required'),
    otherwise: Yup.number().nullable(),
  }),
  rule_name: Yup.string().required('Required').notOneOf(existingRuleNames, 'Must be unique'),
  title: Yup.string().max(100).required('Required'),
  content: Yup.string().max(250),
  sub_organization_ids: subOrgsRequired ? Yup.array().min(1, 'Required') : Yup.array(),
  // Opaque QueryBuilder tree. Structural validation of its leaves is done by the
  // form-level `validate` prop, which needs the fetched field registry.
  conditions_json: Yup.object().nullable(),
  conditions: Yup.array(
    Yup.object().shape({
      criteria_key: Yup.string().required('Required'),
      criteria_types: Yup.array().when('criteria_key', (criteriaKey, currSchema) => {
        if (criteriaKey && conditions[criteriaKey] && conditions[criteriaKey].has_type) {
          return currSchema.min(1, 'At least one type is required');
        }
        return currSchema;
      }),
      condition_key: Yup.string()
        .when('criteria_key', (criteriaKey, currSchema) => {
          if (criteriaKey && conditions[criteriaKey].conditions.length) {
            return currSchema.required('Required');
          }
          return currSchema;
        })
        .nullable(),
      // Required for regular text conditions
      condition_text_value: Yup.string()
        .when(['criteria_key', 'condition_key'], {
          is: (criteriaKey: string, conditionKey: string) =>
            criteriaKey &&
            conditions[criteriaKey] &&
            conditions[criteriaKey].conditions.length &&
            getConditionsWithGroupForSelectedCriteria(conditions[criteriaKey])[conditionKey]?.additionalValueType ===
              'text',
          then: (currSchema) => currSchema.required('Required'),
        })
        .nullable(),
      condition_numeric_value: Yup.number()
        // Percentage conditions are bounded to 0-100%; other numeric fields must be positive.
        .when(['criteria_key', 'condition_key'], {
          is: (criteriaKey: string, conditionKey: string) =>
            criteriaKey &&
            conditions[criteriaKey] &&
            conditions[criteriaKey].conditions.length &&
            getConditionsWithGroupForSelectedCriteria(conditions[criteriaKey])[conditionKey]?.additionalValueType ===
              'numeric' &&
            isPercentageCondition(conditionKey),
          then: (currSchema) =>
            currSchema
              .required('Required')
              .min(0, 'Percentage must be at least 0%')
              .max(100, 'Percentage cannot exceed 100%'),
        })
        .when(['criteria_key', 'condition_key'], {
          is: (criteriaKey: string, conditionKey: string) =>
            criteriaKey &&
            conditions[criteriaKey] &&
            conditions[criteriaKey].conditions.length &&
            getConditionsWithGroupForSelectedCriteria(conditions[criteriaKey])[conditionKey]?.additionalValueType ===
              'numeric' &&
            !isPercentageCondition(conditionKey),
          then: (currSchema) => currSchema.required('Required').min(0, 'Value must be positive'),
        })
        .nullable(),
      condition_schedule_value: Yup.number()
        .when(['criteria_key', 'condition_key'], {
          is: (criteriaKey: string, conditionKey: string) =>
            criteriaKey &&
            conditions[criteriaKey] &&
            conditions[criteriaKey].conditions.length &&
            getConditionsWithGroupForSelectedCriteria(conditions[criteriaKey])[conditionKey]?.additionalValueType ===
              'schedule',
          then: (currSchema) => currSchema.required('Required'),
        })
        .nullable(),
      condition_schedule_timeframe_value: Yup.string()
        .when(['criteria_key', 'condition_key'], {
          is: (criteriaKey: string, conditionKey: string) =>
            criteriaKey &&
            conditions[criteriaKey] &&
            conditions[criteriaKey].conditions.length &&
            getConditionsWithGroupForSelectedCriteria(conditions[criteriaKey])[conditionKey]?.additionalValueType ===
              'schedule',
          then: (currSchema) => currSchema.required('Required'),
        })
        .nullable(),
    })
  ).when(['event_key', 'conditions_json', 'rule_trigger_type'], {
    // Events that demand extra conditions are satisfied by a QueryBuilder tree
    // too while the builder is the rule's editor: there the legacy rows aren't
    // editable (nor persisted), so keeping the min(1) would leave the form
    // permanently unsubmittable. Without the builder the save sends the rows and
    // not the tree, so only a row counts.
    is: (
      eventKey: NotificationRuleFormikEventKey | null,
      conditionsJson: Record<string, unknown> | null,
      ruleTriggerType: string | null | undefined
    ) =>
      !!eventKey?.additional_conditions_are_mandatory &&
      (!shouldUseConditionsQueryBuilder(isQueryBuilderFeatureEnabled, ruleTriggerType) ||
        isEmptyConditionsTree(conditionsJson)),
    then: (currSchema) => currSchema.min(1, 'At least one additional condition must be added'),
  }),
  ...statesValidationSchema,
});

export const mapToKeyAndFilterAll = (list: KeyDescOption[]): string[] =>
  list.map((type) => type.key).filter((typeKey) => typeKey !== 'all');

// Helper function to determine if condition is percentage-based
export const isPercentageCondition = (conditionKey: string | null | undefined) => {
  return (
    conditionKey &&
    [
      'above_percent_of_limit_per_incident',
      'above_percent_of_limit_per_policy',
      'above_percent_of_limit_per_person',
    ].includes(conditionKey)
  );
};

export const convertFormikValuesToServerValues = (
  values: NotificationRuleFormikValues,
  organization: { sub_organizations_enabled: boolean },
  isQueryBuilderFeatureEnabled: boolean
): NotificationRuleCreateJson => {
  const is_all_sub_orgs = values.sub_organization_ids.indexOf('all') > -1 || !organization.sub_organizations_enabled;

  // Strip FE-only Formik state fields that the BE doesn't need
  const {
    id,
    schedule_immediate_or_custom,
    due_date_immediate_or_custom,
    time_based_condition,
    time_based_directions,
    time_based_channels,
    time_based_category_ids,
    time_based_stage_keys,
    time_based_recipient,
    ...serverValues
  } = values;

  const baseValues = {
    ...serverValues,
    is_all_sub_orgs,
    sub_organization_ids: is_all_sub_orgs ? [] : values.sub_organization_ids.filter((id): id is number => id !== 'all'),
    scope_types: mapToKeyAndFilterAll(values.scope_types),
    scope_sub_types: mapToKeyAndFilterAll(values.scope_sub_types || []),
    is_due_date_immediate: values.due_date_immediate_or_custom === 'immediate',
    rule_trigger_type: values.rule_trigger_type || 'event_based',
  };

  // Handle time-based rules differently
  if (values.rule_trigger_type === 'time_based') {
    // Compose criteria_filters from separate Formik fields
    const criteriaFilters: {
      directions?: string[];
      channels?: string[];
      category_ids?: number[];
      stage_keys?: string[];
      recipient?: string[];
    } = {};
    const directions = (values.time_based_directions || []).filter((v) => v !== 'all');
    const channels = (values.time_based_channels || []).filter((v) => v !== 'all');
    const categoryIds = (values.time_based_category_ids || [])
      .filter((v) => v !== 'all')
      .map(Number)
      .filter((id) => !Number.isNaN(id));
    const stageKeys = (values.time_based_stage_keys || []).filter((v) => v !== 'all');
    const recipients = (values.time_based_recipient || []).filter((v) => v !== 'all');
    if (directions.length > 0) criteriaFilters.directions = directions;
    if (channels.length > 0) criteriaFilters.channels = channels;
    if (categoryIds.length > 0) criteriaFilters.category_ids = categoryIds;
    if (stageKeys.length > 0) criteriaFilters.stage_keys = stageKeys;
    // Singular `recipient` matches the BE query-builder shape.
    if (recipients.length > 0) criteriaFilters.recipient = recipients;

    return {
      ...baseValues,
      event_key: null,
      event_criteria_types: [],
      is_immediate: true,
      // Time-based rules don't have custom schedules, clear any stale values
      schedule_number: null,
      schedule_timeframe: null,
      is_due_date_immediate: true,
      due_date_number: null,
      due_date_timeframe: null,
      // Time-based rules are out of the QueryBuilder's scope — their single
      // condition row is authored by TimeBasedConditionFormik.
      conditions_json: null,
      conditions: values.time_based_condition
        ? [
            {
              criteria_key: values.time_based_condition.criteria_key,
              criteria_types: [],
              criteria_filters: criteriaFilters,
              condition_schedule_value: Number(values.time_based_condition.condition_schedule_value),
              condition_schedule_timeframe_value: 'days',
            },
          ]
        : [],
    };
  }

  // Event-based rules (existing behavior)
  const { conditions_json, ...eventBasedValues } = {
    ...baseValues,
    event_key: values.event_key?.key,
    event_criteria_types: mapToKeyAndFilterAll(values.event_criteria_types),
    is_immediate: values.schedule_immediate_or_custom === 'immediate',
  };
  const legacyConditions = values.conditions.map((condition) => ({
    ...condition,
    criteria_types: mapToKeyAndFilterAll(condition.criteria_types),
  }));

  // Without the builder the dialog edits the legacy rows, so the rule is saved
  // with them. The tree the rule carries alongside (derived from those rows, or
  // written while the flag was on) is left out: the BE derives it again from the
  // rows, and leaves a tree untouched when the payload does not mention it.
  if (!shouldUseConditionsQueryBuilder(isQueryBuilderFeatureEnabled, values.rule_trigger_type)) {
    return { ...eventBasedValues, conditions: legacyConditions };
  }

  const hasConditionsTree = !isEmptyConditionsTree(conditions_json);

  return {
    ...eventBasedValues,
    conditions_json: hasConditionsTree ? conditions_json : null,
    // A tree is authored in the builder, which offers no way to edit the legacy
    // rows, so persisting them alongside would be dead weight that only misleads
    // the next reader. Under the flag the BE decides such a rule by its tree.
    conditions: hasConditionsTree ? [] : legacyConditions,
  };
};

export const convertPreDefinedFormikValuesToServerValues = (
  values: NotificationConfigurationFormikFields
): UpdatePreDefinedNotificationJson => {
  const predefinedConditions =
    values.predefined_notification_conditions && Object.keys(values.predefined_notification_conditions).length > 0
      ? values.predefined_notification_conditions
      : null;

  // Only the fields the pre-defined PATCH endpoint consumes. notification_key is
  // added downstream by the caller/handler, so it is intentionally omitted here.
  return {
    is_disabled: values.is_disabled,
    is_all_states: values.is_all_states,
    states: values.states,
    priority: values.priority,
    category_id: values.category_id,
    is_due_date_immediate: values.due_date_immediate_or_custom === 'immediate',
    due_date_number: values.due_date_number,
    due_date_timeframe: values.due_date_timeframe,
    predefined_notification_conditions: predefinedConditions,
  };
};

export const convertServerConditionToFormikValues = (condition: ServerRuleCondition, conditions: CriteriaConfigMap) => {
  const criteriaTypesWithoutAll = splitListAndFindObjects(
    condition.criteria_types,
    conditions[condition.criteria_key].types
  );

  return {
    ...condition,
    criteria_types: criteriaTypesWithoutAll.length ? criteriaTypesWithoutAll : [ALL_OPTION],
  };
};

export const splitListAndFindObjects = (
  joinedKeys: string | null | undefined,
  objectsList: KeyDescOption[] | undefined
): KeyDescOption[] => {
  const keysList = joinedKeys ? joinedKeys.split(', ') : [];
  return keysList
    .map((key) => objectsList?.find((object) => object.key.trim() === key.trim()))
    .filter((item): item is KeyDescOption => !!item);
};

// Produces Formik rule values; the concrete shape depends on the rule's event- vs time-based trigger.
export const convertServerRuleToFormikValues = (
  rule: ServerNotificationRule,
  conditions: CriteriaConfigMap,
  events: EventConfig[],
  incidentTypesMap: Record<string, string>,
  coveragesMap: Record<string, string>
): Record<string, unknown> => {
  const scopeTypesWithoutAll = rule.scope_types.length
    ? rule.scope_types.split(', ').map((type) => ({
        key: type,
        desc: getScopeTypeDescription(type, rule.scope, incidentTypesMap, coveragesMap),
      }))
    : [];
  const scopeSubTypesWithoutAll =
    rule.scope_sub_types && rule.scope_sub_types.length
      ? rule.scope_sub_types
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
          .map((subTypeKey) => ({
            key: subTypeKey,
            desc: subTypeKey,
          }))
      : [];
  const selectedEventConfig = events.find((event) => rule.event_key && event.key === rule.event_key);
  const eventCriteriaTypesWithoutAll = splitListAndFindObjects(rule.event_criteria_types, selectedEventConfig?.types);

  const baseValues = {
    ...rule,
    sub_organization_ids: rule.is_all_sub_orgs ? ['all'] : rule.sub_organization_ids,
    scope_types: scopeTypesWithoutAll.length ? scopeTypesWithoutAll : [ALL_OPTION],
    scope_sub_types: scopeSubTypesWithoutAll.length ? scopeSubTypesWithoutAll : [ALL_OPTION],
    organization_unit_id: rule.organization_unit_id,
    due_date_immediate_or_custom: rule.is_due_date_immediate !== false ? 'immediate' : 'custom',
    rule_trigger_type: rule.rule_trigger_type || 'event_based',
    time_based_condition: null,
  };

  // Handle time-based rules
  if (rule.rule_trigger_type === 'time_based') {
    const timeCondition = rule.conditions.find(
      (c) => c.criteria_key === 'no_notes' || c.criteria_key === 'no_communication'
    );

    const filters = (timeCondition?.criteria_filters || {}) as {
      directions?: string[];
      channels?: string[];
      category_ids?: (string | number)[];
      stage_keys?: string[];
      recipient?: string[];
    };

    // Use string keys for MultiSelectTextFieldFormik
    const directions = filters.directions || [];
    const channels = filters.channels || [];
    const categoryIds = (filters.category_ids || []).map(String);
    const stageKeys = filters.stage_keys || [];
    const recipients = filters.recipient || [];

    return {
      ...baseValues,
      event_key: null,
      event_criteria_types: [ALL_OPTION],
      schedule_immediate_or_custom: 'immediate',
      conditions: [],
      // Time-based rules are out of the QueryBuilder's scope; drop any tree so
      // the dialog never opens the builder for one.
      conditions_json: null,
      time_based_condition: timeCondition
        ? {
            criteria_key: timeCondition.criteria_key,
            criteria_filters: filters,
            condition_schedule_value: timeCondition.condition_schedule_value,
          }
        : null,
      time_based_directions: directions.length ? directions : ['all'],
      time_based_channels: channels.length ? channels : ['all'],
      time_based_category_ids: categoryIds.length ? categoryIds : ['all'],
      time_based_stage_keys: stageKeys.length ? stageKeys : ['all'],
      time_based_recipient: recipients.length ? recipients : ['all'],
    };
  }

  // Event-based rules (existing behavior)
  return {
    ...baseValues,
    event_key: events.find((event) => event.key === rule.event_key),
    event_criteria_types: eventCriteriaTypesWithoutAll.length ? eventCriteriaTypesWithoutAll : [ALL_OPTION],
    schedule_immediate_or_custom: rule.is_immediate ? 'immediate' : 'custom',
    conditions: rule.conditions.map((condition) => convertServerConditionToFormikValues(condition, conditions)),
  };
};

export const convertServerPreDefinedToFormikValues = (notification: PreDefinedNotification) => ({
  ...notification,
  due_date_immediate_or_custom: notification.is_due_date_immediate !== false ? 'immediate' : 'custom',
});

export const CONDITION_FIELD_NAME = 'conditions';

export const BASE_CONDITION_FIELD = {
  criteria_key: '',
  criteria_types: [ALL_OPTION],
  condition_key: null,
  condition_text_value: null,
  condition_numeric_value: null,
  condition_schedule_value: null,
  condition_schedule_timeframe_value: null,
};

export const getConditionsWithGroupForSelectedCriteria = (
  selectedCriteria: CriteriaConfig | undefined
): Record<string, ConditionWithGroupAttributes> =>
  selectedCriteria?.conditions?.reduce<Record<string, ConditionWithGroupAttributes>>((merged, conditionsGroup) => {
    return {
      ...merged,
      ..._.mapValues(conditionsGroup.options, (option) => ({
        ...option,
        additionalValueType: conditionsGroup.condition_value_type,
        textValues: conditionsGroup.text_condition_values,
      })),
    };
  }, {}) || {};

export const joinDescriptionsOfObjectsList = (objectsList: { desc?: string }[]): string =>
  objectsList.map((item) => item.desc).join(', ');

export const STATES_SELECT_OPTIONS = Object.entries(COUNTRY_TO_STATE_MAP.US)
  .filter(([key]) => Boolean(key))
  .map(([key, value]) => ({ value: key, label: value, id: key }))
  .sort((a, b) => a.label.localeCompare(b.label));

// The states are US states, so only a US organization can limit a notification to some of them. The
// hospitality and Boost organizations cannot either: the server ignores a state restriction for them.
export const isNotificationStatesScopeEnabled = (
  organization: Pick<OrganizationModel, 'locale' | 'legacy_org_features'>
): boolean =>
  isOrganizationUsOrg(organization) &&
  !isLegacyOrgFeatureEnabled(organization, LEGACY_ORG_FEATURES.HOSPITALITY_BOOST_NO_NOTIFICATION_STATE_RESTRICTION);
